import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeftRight,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Tag,
  Undo2,
} from "lucide-react";
import type { CurrentUser } from "@/features/auth/currentUserTypes";
import { todaySaoPaulo } from "@/lib/dates";
import {
  formatDashboardCount,
  formatDashboardCurrency,
  formatDashboardPercent,
  formatDashboardUnitQuantity,
} from "@/features/dashboard/metricFormatters";
import type { DashboardPeriodState } from "@/features/dashboard/period";
import type {
  ClientDashboardAnalytics,
  ClientDashboardOperations,
  ClientStoreDashboardAnalytics,
  ClientStoreDashboardOperations,
  DashboardCountMetric,
  DashboardMoneyMetric,
  DashboardPercentMetric,
  DashboardSectionName,
} from "@/features/dashboard/types";
import {
  useAvailableQuotationPublications,
  useEffectiveQuotation,
  useEffectiveQuotationItems,
} from "@/features/quotation/hooks";
import { resolveDashboardQuotationLink } from "@/features/quotation/dashboardLink";
import {
  getQuotationBasePriceSuffix,
  getQuotationBasePriceValue,
} from "@/features/quotation/unitLabels";
import { useOrderSummaries } from "@/features/order/hooks";
import { formatOrderReference } from "@/features/order/display";
import { isOperationalOrderSummary } from "@/features/order/types";
import { formatBRL } from "@/lib/timeAgo";
import { SuperPeriod } from "./SuperPeriod";
import { ClientTrend } from "./ClientTrend";
import "./SuperDashboard.css";
import "./ClientDashboard.css";

export type ClientDashboardPanelRequest =
  | { mode: "ranking"; kind: "stores"; sort: "value"; title: string }
  | {
      mode: "detail";
      kind: "store";
      id: string;
      title: string;
      returnTo: { kind: "stores"; sort: "value" };
    }
  | {
      mode: "price_drop";
      item: import("@/components/dashboard/BonPrecosCard").BonPrecoItem;
    }
  | { mode: "quality" };

type Analytics = ClientDashboardAnalytics | ClientStoreDashboardAnalytics;
type Operations = ClientDashboardOperations | ClientStoreDashboardOperations;
export type ClientDashboardProps = {
  user: CurrentUser;
  selectedStoreId?: string;
  authorizedStores?: { id: string; name: string }[];
  hasMoreStores?: boolean;
  loadingMoreStores?: boolean;
  onLoadMoreStores?: () => void;
  onSelectStore?: (id: string) => void;
  analytics: Analytics;
  operations?: Operations;
  period: DashboardPeriodState;
  pendingPeriod?: DashboardPeriodState | null;
  updating?: boolean;
  onPeriodChange: (period: DashboardPeriodState) => void;
  onNavigate: (
    path:
      | "/pedidos"
      | "/cargas"
      | { to: "/pedidos"; search: { orderId: string } },
  ) => void;
  onOpenPanel: (request: ClientDashboardPanelRequest) => void;
  onRetryAnalytics: () => void;
};

const money = (cents: number) => formatDashboardCurrency(cents).full;
const count = (value: number) => formatDashboardCount(value).full;
const sectionStatus = (analytics: Analytics, section: DashboardSectionName) =>
  analytics.section_status?.find((item) => item.section === section)?.status;
const unavailable = (error = false, retry?: () => void) => (
  <p className="lnk209-empty" role="status">
    {error
      ? "Falha ao carregar esta seção."
      : "Dados indisponíveis para esta seção."}
    {error && retry && <button onClick={retry}>Tentar novamente</button>}
  </p>
);
const empty = (
  <p className="lnk209-empty">Sem dados observados neste período.</p>
);
const labelDay = (day: string) =>
  `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}`;
const unitNames: Record<string, string> = {
  kg: "Peso avulso",
  cx: "Caixas",
  bdj: "Bandejas",
  sc: "Sacos",
  un: "Unidades",
};
const orderStatusLabels: Record<string, string> = {
  open: "Aberto",
  concluido: "Confirmado",
  carregamento: "Em carregamento",
  change_requested: "Alteração solicitada",
  encerrado: "Encerrado",
  cancelled: "Cancelado",
};

function Kpi({
  label,
  metric,
  current,
  description,
  onClick,
  testId,
}: {
  label: string;
  metric: DashboardMoneyMetric | DashboardCountMetric | DashboardPercentMetric;
  current?: boolean;
  description?: string;
  onClick: () => void;
  testId?: string;
}) {
  const comparison =
    metric.status === "available" ? metric.comparison : undefined;
  return (
    <article>
      <span className="lnk209-kpi-index">{label.toUpperCase()}</span>
      <strong data-testid={testId}>
        {metric.status !== "available"
          ? "—"
          : metric.unit === "cents"
            ? money(metric.value)
            : metric.unit === "count"
              ? count(metric.value)
              : formatDashboardPercent(metric.value).full}
      </strong>
      {current ? (
        <span className="lnk209-no-compare">operação atual</span>
      ) : comparison ? (
        <span
          className={`lnk209-delta ${comparison.direction === "down" ? "negative" : comparison.direction === "same" ? "neutral" : "positive"}`}
        >
          {comparison.direction === "down"
            ? "↘ −"
            : comparison.direction === "same"
              ? "→ "
              : "↗ +"}
          {formatDashboardPercent(comparison.percentage).full.replace(
            /^[+-]/,
            "",
          )}
        </span>
      ) : (
        <span className="lnk209-no-compare" aria-label="Sem base comparável">
          —
        </span>
      )}
      {metric.status !== "available" && (
        <span className="lnk209-kpi-unavailable">
          {metric.status === "error" ? "Falha nos dados" : "Dado indisponível"}
        </span>
      )}
      <button
        className="lnk209-kpi-link"
        aria-label={`Abrir ${label}`}
        title={description}
        disabled={metric.status !== "available"}
        onClick={onClick}
      />
    </article>
  );
}

function LoadDonut({
  analytics,
  blocked,
  retry,
}: {
  analytics: Analytics;
  blocked: boolean;
  retry: () => void;
}) {
  const [active, setActive] = useState<string | null>(null);
  const situation = analytics.sections.load_situation;
  const status = sectionStatus(analytics, "load_situation");
  const states = (
    [
      { state: "concluded", label: "Concluídas", color: "#327554" },
      { state: "in_progress", label: "Em carregamento", color: "#d5a145" },
      { state: "not_started", label: "Pendentes", color: "#a8b6a7" },
    ] as const
  ).map((item) => ({
    ...item,
    count: situation.states.find((row) => row.state === item.state)?.count ?? 0,
  }));
  let offset = 0;
  return (
    <section
      className="lnk209-panel lnk209-load"
      aria-label="Situação das cargas"
      id="cargas"
    >
      <header>
        <h2>Situação das cargas</h2>
        <span className="lnk209-panel-meta">por quantidade</span>
      </header>
      {blocked || (status && status !== "available") ? (
        unavailable(status === "error", retry)
      ) : situation.total ? (
        <div className="lnk209-load-body">
          <div
            className="lnk209-donut"
            role="img"
            aria-label={`${situation.total} cargas: ${states.map((item) => `${item.count} ${item.label}`).join(", ")}`}
          >
            <svg viewBox="0 0 120 120" aria-hidden="true">
              <circle
                cx="60"
                cy="60"
                r="48"
                fill="none"
                stroke="var(--track)"
                strokeWidth="14"
              />
              {states.map((item) => {
                const start = offset;
                const slice = (item.count / situation.total) * 100;
                offset += slice;
                return (
                  <circle
                    key={item.state}
                    cx="60"
                    cy="60"
                    r="48"
                    fill="none"
                    stroke={item.color}
                    strokeWidth="14"
                    pathLength="100"
                    strokeDasharray={`${slice} ${100 - slice}`}
                    strokeDashoffset={-start}
                    transform="rotate(-90 60 60)"
                  />
                );
              })}
            </svg>
            <div>
              <strong>{count(situation.total)}</strong>
              <small>CARGAS</small>
            </div>
          </div>
          <div className="lnk209-legend">
            {states.map((item) => (
              <button
                key={item.state}
                aria-label={item.label}
                aria-pressed={active === item.label}
                onMouseEnter={() => setActive(item.label)}
                onFocus={() => setActive(item.label)}
                onClick={() => setActive(item.label)}
              >
                <i style={{ background: item.color }} />
                <span>{item.label}</span>
                <strong>{count(item.count)}</strong>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="lnk209-empty">
          Nenhuma carga registrada neste período. Explore outra data.
        </p>
      )}
    </section>
  );
}

export function StoreSwitch({
  stores,
  selected,
  onSelect,
  hasMore,
  loadingMore,
  onLoadMore,
}: {
  stores: { id: string; name: string }[];
  selected: string;
  onSelect: (id: string) => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    menu.current
      ?.querySelectorAll<HTMLButtonElement>("[role=menuitemradio]")
      [focus]?.focus();
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("keydown", key);
    document.addEventListener("pointerdown", outside);
    return () => {
      document.removeEventListener("keydown", key);
      document.removeEventListener("pointerdown", outside);
    };
  }, [open, focus]);
  return (
    <div
      className="lnk209-store-switch"
      role="group"
      aria-label="Lojas autorizadas"
      ref={root}
    >
      <button
        ref={trigger}
        className="lnk209-store-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Loja selecionada: ${stores.find((store) => store.id === selected)?.name ?? "Selecione"}`}
        onClick={() => {
          setFocus(
            Math.max(
              0,
              stores.findIndex((store) => store.id === selected),
            ),
          );
          setOpen(!open);
        }}
        onKeyDown={(event) => {
          if (!open && ["ArrowDown", "ArrowUp"].includes(event.key)) {
            event.preventDefault();
            setFocus(
              Math.max(
                0,
                stores.findIndex((store) => store.id === selected),
              ),
            );
            setOpen(true);
          }
        }}
      >
        <span className="lnk209-store-trigger-text">
          {stores.find((store) => store.id === selected)?.name ?? "Selecione"}
        </span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div
          className="lnk209-store-menu"
          role="menu"
          aria-label="Escolher loja"
          ref={menu}
        >
          {stores.map((store, index) => (
            <button
              key={store.id}
              role="menuitemradio"
              aria-checked={selected === store.id}
              onClick={() => {
                onSelect(store.id);
                setOpen(false);
                trigger.current?.focus();
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                  event.preventDefault();
                  setFocus(
                    (index +
                      (event.key === "ArrowDown" ? 1 : stores.length - 1)) %
                      stores.length,
                  );
                }
              }}
            >
              {store.name}
            </button>
          ))}
          {hasMore && (
            <button role="menuitem" disabled={loadingMore} onClick={onLoadMore}>
              {loadingMore ? "Carregando lojas…" : "Carregar mais lojas"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function ClientDashboard({
  user,
  selectedStoreId,
  authorizedStores = [],
  hasMoreStores = false,
  loadingMoreStores = false,
  onLoadMoreStores,
  onSelectStore,
  analytics,
  operations,
  period,
  pendingPeriod,
  updating,
  onPeriodChange,
  onNavigate,
  onOpenPanel,
  onRetryAnalytics,
}: ClientDashboardProps) {
  const navigate = useNavigate();
  const isStore = analytics.role === "client_store";
  const stores = authorizedStores;
  const selectedStore = stores.find(({ id }) => id === selectedStoreId);
  const storeId = selectedStore?.id ?? "";
  const blocked = isStore && !selectedStore;
  const publicationQuery = useAvailableQuotationPublications();
  const publication = publicationQuery.data?.find(
    (item) => item.status === "completed",
  );
  const date = publication?.date.slice(0, 10);
  const quoteParams = isStore
    ? { date, storeId: storeId || undefined }
    : { date, clientId: user.client_id };
  const quoteEnabled = Boolean(date && (isStore ? storeId : user.client_id));
  const effectiveQuotationQuery = useEffectiveQuotation(quoteParams, {
    enabled: quoteEnabled,
  });
  const effectiveQuery = useEffectiveQuotationItems(quoteParams, {
    enabled: quoteEnabled,
  });
  const quoteLink =
    publication && !effectiveQuery.isPlaceholderData && user.client_id
      ? resolveDashboardQuotationLink(
          isStore
            ? { date: date!, storeId, quotationId: publication.id }
            : {
                date: date!,
                clientId: user.client_id,
                quotationId: publication.id,
              },
          publicationQuery.data ?? [],
          { clientId: user.client_id, stores },
          effectiveQuotationQuery.data,
          effectiveQuery.data?.quotation.id,
        )
      : null;
  const offers = quoteLink
    ? (effectiveQuery.data?.items.filter((item) => item.is_offer).slice(0, 3) ??
      [])
    : [];
  const ordersQuery = useOrderSummaries({
    limit: 3,
    offset: 0,
    enabled: isStore && Boolean(storeId),
    filters: {
      ...(storeId ? { store_id: storeId } : {}),
      ...(analytics.period.from ? { date_from: analytics.period.from } : {}),
      date_to: analytics.period.to,
    },
  });
  const orders = ordersQuery.data?.items ?? [];
  const activeDate = new Date(`${todaySaoPaulo()}T12:00:00Z`);
  const openValue: DashboardMoneyMetric =
    !operations ||
    operations.section_status?.some(
      (item) => item.section === "open_loads" && item.status !== "available",
    )
      ? {
          status: "unavailable",
          unit: "cents",
          reason_code: "current_operations",
        }
      : {
          status: "available",
          unit: "cents",
          value: operations.open_loads.total_value_cents,
        };
  const unavailableMoney: DashboardMoneyMetric = {
    status: "unavailable",
    unit: "cents",
    reason_code: "store_scope_unavailable",
  };
  const unavailableCount: DashboardCountMetric = {
    status: "unavailable",
    unit: "count",
    reason_code: "store_scope_unavailable",
  };
  const unavailablePercent: DashboardPercentMetric = {
    status: "unavailable",
    unit: "percent",
    reason_code: "store_scope_unavailable",
  };
  const section = (name: DashboardSectionName) =>
    blocked ||
    Boolean(
      sectionStatus(analytics, name) &&
      sectionStatus(analytics, name) !== "available",
    );
  return (
    <main
      className="lnk209-super lnk209-client-preview lnk209-app"
      data-testid="client-dashboard"
      aria-busy={updating || undefined}
    >
      <div className="lnk209-content">
        <div className="lnk209-inner">
          <header className="lnk209-main-head">
            <h1>Painel de compras</h1>
            <span className="lnk209-header-context">
              <span>
                {period.period === "custom"
                  ? "PERÍODO PERSONALIZADO"
                  : activeDate
                      .toLocaleDateString("pt-BR", {
                        weekday: "long",
                        timeZone: "UTC",
                      })
                      .toUpperCase()}
              </span>
              <strong>
                {period.period === "custom" && period.from && period.to
                  ? `${labelDay(period.from)}–${labelDay(period.to)}`
                  : activeDate
                      .toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        timeZone: "UTC",
                      })
                      .toUpperCase()
                      .replaceAll(".", "")
                      .replaceAll(" DE ", " ")}
              </strong>
            </span>
          </header>
          <div className="lnk209-client-period">
            <SuperPeriod
              period={period}
              pendingPeriod={pendingPeriod}
              metadata={analytics.period}
              updating={updating}
              onChange={onPeriodChange}
            />
            {isStore && (
              <StoreSwitch
                stores={stores}
                selected={storeId}
                onSelect={(id) => onSelectStore?.(id)}
                hasMore={hasMoreStores}
                loadingMore={loadingMoreStores}
                onLoadMore={() => onLoadMoreStores?.()}
              />
            )}
          </div>
          <section
            className="lnk209-kpis"
            aria-label="Indicadores principais de compras"
            data-testid="client-dashboard-headlines"
          >
            <Kpi
              label="Compras no período"
              metric={blocked ? unavailableMoney : analytics.metrics.purchases}
              description="Quantidades recebidas no período, mesmo com a carga em andamento."
              testId="client-purchases"
              onClick={() => onNavigate("/pedidos")}
            />
            <Kpi
              label="Valor em aberto"
              metric={blocked ? unavailableMoney : openValue}
              description="Quantidades ainda aguardadas, independentemente do período selecionado."
              current
              onClick={() => onNavigate("/cargas")}
            />
            <Kpi
              label="Pedidos no período"
              metric={blocked ? unavailableCount : analytics.metrics.orders}
              onClick={() => onNavigate("/pedidos")}
            />
            <Kpi
              label="Sem ocorrência"
              metric={
                blocked
                  ? unavailablePercent
                  : analytics.metrics.no_occurrence_rate
              }
              onClick={() => onOpenPanel({ mode: "quality" })}
            />
          </section>
          <div
            className="lnk209-analysis"
            data-testid="client-dashboard-analysis"
          >
            {section("purchase_evolution") ? (
              <section
                className="lnk209-panel lnk209-trend"
                aria-label="Evolução de compras"
              >
                <header>
                  <h2>Evolução de compras</h2>
                </header>
                {unavailable(
                  sectionStatus(analytics, "purchase_evolution") === "error",
                  onRetryAnalytics,
                )}
              </section>
            ) : (
              <ClientTrend
                points={analytics.sections.purchase_evolution}
                period={period}
                metadata={analytics.period}
                onHistoryChange={(history) =>
                  onPeriodChange({ ...period, history })
                }
              />
            )}
            <LoadDonut
              analytics={analytics}
              blocked={blocked}
              retry={onRetryAnalytics}
            />
          </div>
          <div
            className="lnk209-client-priority"
            data-testid="client-dashboard-priority-insights"
          >
            <section
              className="lnk209-panel lnk209-promotions"
              aria-label="Ofertas da cotação"
            >
              <header>
                <h2>
                  <Tag size={20} strokeWidth={1.8} aria-hidden="true" />
                  Ofertas da cotação
                </h2>
                <button
                  className="lnk209-view-all"
                  disabled={!quoteLink}
                  onClick={() =>
                    quoteLink &&
                    void navigate({
                      to: "/cotacoes",
                      search: {
                        date: quoteLink.date,
                        storeId: quoteLink.storeId,
                        clientId: quoteLink.clientId,
                        quotationId: quoteLink.quotationId,
                      },
                    })
                  }
                >
                  Ver cotação <ChevronRight size={15} aria-hidden="true" />
                </button>
              </header>
              {publicationQuery.isPending ||
              (quoteEnabled &&
                (effectiveQuery.isPending ||
                  effectiveQuotationQuery.isPending)) ||
              effectiveQuery.isPlaceholderData ? (
                <p className="lnk209-empty">Carregando ofertas…</p>
              ) : publicationQuery.isError ||
                effectiveQuery.isError ||
                effectiveQuotationQuery.isError ? (
                <p className="lnk209-empty" role="status">
                  Não foi possível carregar as ofertas.
                </p>
              ) : offers.length ? (
                <div className="lnk209-offer-list">
                  {offers.map((offer, index) => (
                    <button
                      className="lnk209-offer-row"
                      key={offer.id}
                      onClick={() =>
                        quoteLink &&
                        void navigate({
                          to: "/cotacoes",
                          search: {
                            date: quoteLink.date,
                            storeId: quoteLink.storeId,
                            clientId: quoteLink.clientId,
                            quotationId: quoteLink.quotationId,
                            itemId: offer.id,
                          },
                        })
                      }
                    >
                      <span className="lnk209-rank">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="lnk209-offer-name">
                        <strong>{offer.product_name}</strong>
                      </span>
                      <span className="lnk209-offer-price">
                        <strong>
                          {getQuotationBasePriceValue(offer) > 0
                            ? formatBRL(getQuotationBasePriceValue(offer))
                            : "—"}{" "}
                          <small>
                            / {getQuotationBasePriceSuffix(offer.unit_type)}
                          </small>
                        </strong>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="lnk209-empty">
                  Nenhuma oferta publicada para esta loja.
                </p>
              )}
            </section>
            {isStore ? (
              <section
                className="lnk209-panel lnk209-recent-orders"
                aria-label="Pedidos recentes"
              >
                <header>
                  <h2>Pedidos recentes</h2>
                  <span className="lnk209-panel-meta">
                    {selectedStore?.name ?? "Loja indisponível"}
                  </span>
                </header>
                {!storeId ||
                ordersQuery.isPending ||
                ordersQuery.isPlaceholderData ? (
                  <p className="lnk209-empty">Carregando pedidos…</p>
                ) : ordersQuery.isError ? (
                  unavailable(true)
                ) : orders.length ? (
                  <div className="lnk209-recent-list">
                    {orders
                      .filter(
                        (order) =>
                          order.store_id === storeId &&
                          !isOperationalOrderSummary(order),
                      )
                      .map((order) => (
                        <button
                          key={order.id}
                          className="lnk209-order-row"
                          onClick={() =>
                            onNavigate({
                              to: "/pedidos",
                              search: { orderId: order.id },
                            })
                          }
                        >
                          <span className="lnk209-order-name">
                            <strong>
                              Pedido {formatOrderReference(order.id)}
                            </strong>
                            <small>
                              {orderStatusLabels[order.status] ??
                                "Estado indisponível"}
                            </small>
                          </span>
                          <span className="lnk209-order-value">
                            {labelDay(order.quotation_date.slice(0, 10))} ·{" "}
                            <strong>
                              {"total_price" in order
                                ? formatBRL(order.total_price)
                                : "—"}
                            </strong>
                          </span>
                          <ChevronRight size={16} aria-hidden="true" />
                        </button>
                      ))}
                  </div>
                ) : (
                  empty
                )}
              </section>
            ) : (
              <section
                className="lnk209-panel lnk209-ranking"
                aria-label="Lojas por compras"
              >
                <header>
                  <button
                    className="lnk209-ranking-heading"
                    disabled={section("stores")}
                    onClick={() =>
                      onOpenPanel({
                        mode: "ranking",
                        kind: "stores",
                        sort: "value",
                        title: "Lojas por compras",
                      })
                    }
                  >
                    <h2>Lojas por compras</h2>
                  </button>
                  <button
                    className="lnk209-view-all"
                    disabled={section("stores")}
                    onClick={() =>
                      onOpenPanel({
                        mode: "ranking",
                        kind: "stores",
                        sort: "value",
                        title: "Lojas por compras",
                      })
                    }
                  >
                    Ver mais <ChevronRight size={15} aria-hidden="true" />
                  </button>
                </header>
                {section("stores") ? (
                  unavailable(
                    sectionStatus(analytics, "stores") === "error",
                    onRetryAnalytics,
                  )
                ) : analytics.role === "client" &&
                  analytics.sections.stores.length ? (
                  <div className="lnk209-ranking-list">
                    {analytics.sections.stores
                      .slice(0, 3)
                      .map((store, index) => (
                        <button
                          key={store.id ?? store.name}
                          onClick={() =>
                            store.id
                              ? onOpenPanel({
                                  mode: "detail",
                                  kind: "store",
                                  id: store.id!,
                                  title: store.name,
                                  returnTo: { kind: "stores", sort: "value" },
                                })
                              : onOpenPanel({
                                  mode: "ranking",
                                  kind: "stores",
                                  sort: "value",
                                  title: "Lojas por compras",
                                })
                          }
                        >
                          <span className="lnk209-rank">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          <span className="lnk209-ranking-name">
                            <strong>{store.name}</strong>
                            <small>Compras no período</small>
                          </span>
                          <strong className="lnk209-ranking-value">
                            {money(store.value_cents)}
                          </strong>
                        </button>
                      ))}
                  </div>
                ) : (
                  empty
                )}
              </section>
            )}
          </div>
          <section
            className="lnk209-panel lnk209-client-quality"
            aria-label="Qualidade das cargas"
            data-testid="client-dashboard-quality"
          >
            <header>
              <h2>Qualidade das cargas</h2>
            </header>
            {section("quality_outcomes") ? (
              unavailable(
                sectionStatus(analytics, "quality_outcomes") === "error",
                onRetryAnalytics,
              )
            ) : (
              <div className="lnk209-quality-ledger">
                {(
                  [
                    {
                      label: "Decisões",
                      value: analytics.sections.quality_outcomes.decision_count,
                      icon: ClipboardCheck,
                    },
                    {
                      label: "Substituídos",
                      value:
                        analytics.sections.quality_outcomes.substituted_count,
                      icon: ArrowLeftRight,
                    },
                    {
                      label: "Devolvidos",
                      value: analytics.sections.quality_outcomes.returned_count,
                      icon: Undo2,
                    },
                    {
                      label: "Divergências",
                      value:
                        analytics.sections.quality_outcomes.divergence_count,
                      icon: AlertTriangle,
                    },
                  ] as const
                ).map(({ label, value, icon: Icon }) => (
                  <div key={label}>
                    <span className="lnk209-outcome-name">
                      <Icon size={18} aria-hidden="true" />
                      {label}
                    </span>
                    <strong>{count(value)}</strong>
                  </div>
                ))}
              </div>
            )}
          </section>
          <div
            className="lnk209-breakdowns"
            data-testid="client-dashboard-bottom"
          >
            <section
              className="lnk209-panel lnk209-category-spend"
              aria-label="Compras por categoria"
            >
              <header>
                <h2>Compras por categoria</h2>
              </header>
              {section("category_spend") ? (
                unavailable(
                  sectionStatus(analytics, "category_spend") === "error",
                  onRetryAnalytics,
                )
              ) : analytics.sections.category_spend.length ? (
                <dl>
                  {analytics.sections.category_spend.map((item) => (
                    <div key={item.category_name}>
                      <dt>{item.category_name}</dt>
                      <dd>{money(item.value_cents)}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                empty
              )}
            </section>
            <section className="lnk209-panel" aria-label="Quantidade comprada">
              <header>
                <h2>Quantidade comprada</h2>
              </header>
              {section("purchased_quantities") ? (
                unavailable(
                  sectionStatus(analytics, "purchased_quantities") === "error",
                  onRetryAnalytics,
                )
              ) : analytics.sections.purchased_quantities.length ? (
                <div className="lnk209-unit-ledger">
                  {analytics.sections.purchased_quantities.map(
                    ({ unit, quantity }) => (
                      <div key={unit}>
                        <span className="lnk209-unit-name">
                          {unitNames[unit] ?? unit}
                          <small>{unit}</small>
                        </span>
                        <strong>
                          {formatDashboardUnitQuantity(unit, quantity)?.full ??
                            "—"}
                        </strong>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                empty
              )}
            </section>
          </div>
          {blocked && (
            <p className="lnk209-scope-note" role="status">
              Indicadores indisponíveis para a loja selecionada.
            </p>
          )}
        </div>
      </div>
    </main>
  );
}
